import { useState } from 'react'
import { Button, Modal } from '@/components/ui'
import type { ShipConfirmationSplit } from '../api/types'

export interface LogSplitEstimateModalProps {
  split: ShipConfirmationSplit | null
  isPending: boolean
  onCancel: () => void
  onSubmit: (body: { estimated_date?: string; note: string }) => void
}

/** Split-scoped analog of `LogEstimateModal` — logs a revised (still
 *  unconfirmed) estimate on ONE split without changing its stage. */
export function LogSplitEstimateModal({ split, isPending, onCancel, onSubmit }: LogSplitEstimateModalProps) {
  const [estimatedDate, setEstimatedDate] = useState('')
  const [note, setNote] = useState('')

  if (!split) return null

  const canSubmit = !!note.trim()

  function reset() {
    setEstimatedDate('')
    setNote('')
  }

  return (
    <Modal
      open={split !== null}
      onOpenChange={(next) => {
        if (!next) {
          reset()
          onCancel()
        }
      }}
      tone="info"
      title={`Log estimate update — split ${split.split_index}`}
      description="Record a vendor update for this split without marking it confirmed."
      footer={
        <>
          <Button
            variant="ghost"
            onClick={() => {
              reset()
              onCancel()
            }}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={isPending}
            disabled={!canSubmit}
            onClick={() => onSubmit({ estimated_date: estimatedDate || undefined, note })}
          >
            Log update
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">Revised estimate (optional)</span>
          <input
            type="date"
            value={estimatedDate}
            onChange={(e) => setEstimatedDate(e.target.value)}
            className="rounded-xs border border-border-default bg-surface px-2 py-1 text-sm text-primary focus:border-border-focus focus:outline-none"
          />
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">What did the vendor say?</span>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. vendor says still awaiting vessel space"
            rows={3}
            className="rounded-xs border border-border-default bg-surface px-2 py-1.5 text-sm text-primary placeholder:text-tertiary focus:border-border-focus focus:outline-none"
          />
        </label>
      </div>
    </Modal>
  )
}
